// =============================================================================
// Sales Register — faithful port of POS frmExpressInvoicing + frmPaymentMethod.
// Workflow preserved: code/scan entry -> qty/price/discount -> item grid;
// per-line edit; tax-exempt toggle; subtotal discount; refund mode; split tender.
// =============================================================================
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Search, Trash2, X, Plus, RotateCcw, Percent, Receipt, UserPlus, Ban, Monitor, FileText,
  Pause, History, ClipboardList, KeyRound, Wallet,
} from 'lucide-react'
import {
  searchProducts, lookupProductByCode, getDefaultTaxRate, getNextInvoiceNumber,
  listCustomers, lookupGiftCertificate, redeemGiftCertificate, adjustCustomerStoreCredit,
  adjustCustomerArBalance, getCustomer,
  listRegisters, getRegisterByNumber, zoutReport, type ZoutData,
  saveDraftInvoice, setInvoiceStatus, deleteInvoiceDraft, listResumableInvoices,
  finalizeDraftInvoice, getInvoiceWithItems, type ResumableInvoice,
  type Clerk, voidInvoice,
  getOpenRegisterSession, openRegisterSession, closeRegisterSession,
} from '../services/data'
import { printReceipt, openCashDrawer, printZReport, formatZReportText } from '../services/hardware'
import { printHtml } from '../lib/print'
import { useBarcodeScanner } from '../services/scanner'
import { isStripeTerminalConfigured, chargeOnTerminal, isStripeMotoConfigured, chargeMotoOnReader, type ChargeOptions } from '../services/stripeTerminal'
import { getDeviceRegister, setDeviceRegister, onDeviceRegisterChange, type DeviceRegisterProfile, type PrinterType, type DrawerType } from '../services/register'
import { useAuth } from '../context/AuthContext'
import { useRole } from '../context/RoleContext'
import ManagerOverride from '../components/shared/ManagerOverride'
import ClerkPinModal from '../components/shared/ClerkPinModal'
import { Card, Button } from '../components/shared/ui'
import { currency, productCode, customerLabel } from '../lib/format'
import type { Product, Customer, Register, RegisterSession } from '../types'
import { toast } from 'react-toastify'

// Legacy: product codes 25 (gift cert) and 5 are always non-taxable.
const ALWAYS_EXEMPT_CODES = new Set(['5', '25'])

interface SaleLine {
  uid: string
  product: Product | null
  productId: string | null   // preserves product linkage for resumed drafts
  code: string
  name: string
  qty: number
  price: number
  discountPercent: number
  discountAmount: number
  taxPercent: number
}

let lineSeq = 0
const newUid = () => `L${++lineSeq}`

function lineGross(l: SaleLine) { return l.price * l.qty }
function lineDiscount(l: SaleLine) {
  // Percent (if any) takes precedence and derives the amount, matching the
  // subtotal-discount behavior in frmExpressInvoicing; explicit $ adds on top.
  const pct = l.discountPercent > 0 ? lineGross(l) * (l.discountPercent / 100) : 0
  return Math.min(lineGross(l), pct + l.discountAmount)
}
function lineNet(l: SaleLine) { return lineGross(l) - lineDiscount(l) }
function lineTax(l: SaleLine) { return Math.round(lineNet(l) * (l.taxPercent / 100) * 100) / 100 }

interface OverrideRequest { perm: string; title: string; message: string; run: () => void }

export default function SalesRegister() {
  const { user } = useAuth()
  const { can } = useRole()
  const navigate = useNavigate()
  // Manager-override prompt for restricted in-sale actions (void item, discounts).
  const [override, setOverride] = useState<OverrideRequest | null>(null)
  const guard = useCallback((perm: string, title: string, message: string, run: () => void) => {
    if (can(perm)) { run(); return }
    setOverride({ perm, title, message, run })
  }, [can])
  const [device, setDevice] = useState<DeviceRegisterProfile | null>(getDeviceRegister())
  const [register, setRegister] = useState<Register | null>(null)
  const [signInOpen, setSignInOpen] = useState(false)
  const [zoutOpen, setZoutOpen] = useState(false)
  const [resumeOpen, setResumeOpen] = useState(false)
  const [drawer, setDrawer] = useState<RegisterSession | null>(null)
  const [drawerModal, setDrawerModal] = useState<'open' | 'close' | null>(null)
  // Current in-progress invoice (legacy persists the cart to the DB live so a
  // crash leaves it recoverable). invoiceNumber is shown so the clerk knows it
  // is saved; refs hold the latest values for async save/finalize/unload paths.
  const [invoiceNumber, setInvoiceNumber] = useState<number | null>(null)
  const invoiceIdRef = useRef<string | null>(null)
  const invoiceNumberRef = useRef<number | null>(null)
  const committingRef = useRef(false)   // suppress draft saves during finalize/suspend/clear
  const recoveredRef = useRef(false)    // crash recovery runs once per mount
  const [searchParams, setSearchParams] = useSearchParams()
  const resumeReqRef = useRef<string | null>(null)  // ?resume=<id> deep-link handled once
  // Per-transaction clerk (legacy frmLoginPopUp): pick name + enter PIN before
  // ringing. The chosen clerk is stamped on the invoice (clerk_id).
  const [clerk, setClerk] = useState<Clerk | null>(null)
  const [clerkPromptOpen, setClerkPromptOpen] = useState(false)
  const clerkRef = useRef<Clerk | null>(null)
  const pendingCodeRef = useRef<string | null>(null)  // code/scan to replay after PIN
  const [taxRate, setTaxRate] = useState(0)
  const [taxExempt, setTaxExempt] = useState(false)
  const [refundMode, setRefundMode] = useState(false)
  const [lines, setLines] = useState<SaleLine[]>([])
  const [subDiscountPct, setSubDiscountPct] = useState('')
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [customerOpen, setCustomerOpen] = useState(false)

  // Entry bar fields (txtInputProductCode / Quantity / Price)
  const [code, setCode] = useState('')
  const [qty, setQty] = useState('1')
  const [price, setPrice] = useState('')
  const codeRef = useRef<HTMLInputElement>(null)

  // Product search popover
  const [searchOpen, setSearchOpen] = useState(false)
  const [tenderOpen, setTenderOpen] = useState(false)

  useEffect(() => { getDefaultTaxRate().then(setTaxRate).catch(() => setTaxRate(0)) }, [])
  useEffect(() => { codeRef.current?.focus() }, [])
  // React to device sign-in/out (other tabs / settings) and load the register row.
  useEffect(() => onDeviceRegisterChange(setDevice), [])
  useEffect(() => {
    if (!device) { setRegister(null); setDrawer(null); return }
    getRegisterByNumber(device.registerNumber).then(setRegister).catch(() => setRegister(null))
    getOpenRegisterSession(device.registerNumber).then(setDrawer).catch(() => setDrawer(null))
  }, [device])

  // Per-register Stripe reader/location, looked up from the registers table.
  const readerOpts: ChargeOptions = useMemo(
    () => ({ readerId: register?.stripe_reader_id ?? null, locationId: register?.stripe_location_id ?? null }),
    [register],
  )

  const effectiveTaxPercent = useCallback((p: Product | null) => {
    if (taxExempt) return 0
    const sku = (p?.sku ?? '').toString()
    const scan = (p?.scan_number ?? '').toString()
    if (ALWAYS_EXEMPT_CODES.has(sku) || ALWAYS_EXEMPT_CODES.has(scan)) return 0
    return taxRate
  }, [taxExempt, taxRate])

  // Recompute tax on all lines when tax-exempt toggles.
  useEffect(() => {
    setLines(prev => prev.map(l => ({ ...l, taxPercent: effectiveTaxPercent(l.product) })))
  }, [taxExempt, taxRate, effectiveTaxPercent])

  const addProductLine = useCallback((p: Product) => {
    const q = Math.abs(parseFloat(qty) || 1) * (refundMode ? -1 : 1)
    const pr = parseFloat(price) || (p.sale_price ?? p.retail_price ?? 0)
    setLines(prev => [...prev, {
      uid: newUid(),
      product: p,
      productId: p.id,
      code: productCode(p),
      name: p.name,
      qty: q,
      price: pr,
      discountPercent: 0,
      discountAmount: 0,
      taxPercent: effectiveTaxPercent(p),
    }])
    setCode(''); setQty('1'); setPrice('')
    codeRef.current?.focus()
  }, [qty, price, refundMode, effectiveTaxPercent])

  const addManualLine = useCallback(() => {
    const q = Math.abs(parseFloat(qty) || 1) * (refundMode ? -1 : 1)
    setLines(prev => [...prev, {
      uid: newUid(), product: null, productId: null, code: 'MISC', name: 'Manual Item',
      qty: q, price: parseFloat(price) || 0, discountPercent: 0, discountAmount: 0,
      taxPercent: taxExempt ? 0 : taxRate,
    }])
    setCode(''); setQty('1'); setPrice('')
    codeRef.current?.focus()
  }, [qty, price, refundMode, taxExempt, taxRate])

  // Clerk gate (legacy frmLoginPopUp): a transaction can't be rung until a clerk
  // has authenticated. If none yet, stash the code/scan and prompt for the PIN;
  // it is replayed after a successful sign-in.
  const requireClerk = useCallback((replayCode: string | null): boolean => {
    if (clerkRef.current) return true
    pendingCodeRef.current = replayCode
    setClerkPromptOpen(true)
    return false
  }, [])

  // Look up a code and add it (manual item for '-1', else product lookup).
  const processCode = useCallback(async (c: string, fromScan = false) => {
    if (c === '-1') { addManualLine(); return }
    try {
      const p = await lookupProductByCode(c)
      if (p) addProductLine(p)
      else if (fromScan) toast.warn(`No product found for scan "${c}"`)
      else { setCode(c); setSearchOpen(true) }  // legacy SearchInventory
    } catch {
      toast.error('Lookup failed')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addProductLine])

  const submitCode = async () => {
    const c = code.trim()
    if (!c) return
    if (!requireClerk(c)) return
    processCode(c)
  }

  // Barcode / RFID scanner: when focus isn't already in the code box, a hardware
  // scan looks up the product and adds it directly (faithful to scanning at the
  // register). Disabled while a modal is open.
  const scanAndAdd = useCallback(async (scanned: string) => {
    const c = scanned.trim()
    if (!c) return
    if (!requireClerk(c)) return
    processCode(c, true)
  }, [processCode, requireClerk])
  useBarcodeScanner(scanAndAdd, { enabled: !tenderOpen && !searchOpen && !clerkPromptOpen })

  const updateLine = (uid: string, patch: Partial<SaleLine>) =>
    setLines(prev => prev.map(l => l.uid === uid ? { ...l, ...patch } : l))
  const doRemoveLine = (uid: string) => setLines(prev => prev.filter(l => l.uid !== uid))
  // Voiding a line is permission-gated (legacy perm_void_item); cashiers without
  // it need a manager override.
  const removeLine = (uid: string) => guard('perm_void_item', 'Void Item',
    'You do not have permission to void an item. A manager must authorize.', () => doRemoveLine(uid))

  // Reset the local cart only (used after complete / suspend). Does NOT touch DB.
  // Clears the clerk too, so the next transaction re-prompts for a PIN — faithful
  // to the legacy StartNewTransaction showing frmLoginPopUp every time.
  const resetCart = useCallback(() => {
    setLines([]); setSubDiscountPct(''); setTaxExempt(false); setCustomer(null)
    invoiceIdRef.current = null; invoiceNumberRef.current = null; setInvoiceNumber(null)
    clerkRef.current = null; setClerk(null)
    codeRef.current?.focus()
  }, [])

  // Successful PIN verification: set the clerk, then replay any queued code/scan.
  const onClerkVerified = useCallback((c: Clerk) => {
    clerkRef.current = c
    setClerk(c)
    setClerkPromptOpen(false)
    const pending = pendingCodeRef.current
    pendingCodeRef.current = null
    if (pending != null) processCode(pending)
    codeRef.current?.focus()
  }, [processCode])

  // Clear button: discard the in-progress draft from the DB, then reset.
  const clearSale = useCallback(async () => {
    const id = invoiceIdRef.current
    committingRef.current = true
    try { if (id) await deleteInvoiceDraft(id) }
    catch (e) { console.error('discard draft failed', e) }
    finally { committingRef.current = false }
    resetCart()
  }, [resetCart])

  // Apply a subtotal discount % to every positive-qty line (frmExpressInvoicing tbxDisPer).
  const doApplySubDiscount = () => {
    const pct = Math.min(100, parseFloat(subDiscountPct) || 0)
    setLines(prev => prev.map(l => l.qty > 0 ? { ...l, discountPercent: pct } : l))
  }
  const applySubDiscount = () => guard('perm_sub_total_discount', 'Subtotal Discount',
    'You do not have permission to apply a subtotal discount. A manager must authorize.', doApplySubDiscount)

  const totals = useMemo(() => {
    const totalQty = lines.reduce((s, l) => s + l.qty, 0)
    const subtotal = lines.reduce((s, l) => s + lineNet(l), 0)
    const tax = lines.reduce((s, l) => s + lineTax(l), 0)
    return { totalQty, subtotal, tax, grand: subtotal + tax }
  }, [lines])

  // ---- In-progress draft persistence (legacy "In Progress" invoice) ----------
  // Build invoice_items rows for the current cart (also used at finalize).
  const buildItemRows = useCallback(() => lines.map(l => ({
    product_id: l.product?.id ?? l.productId ?? null,
    product_name: l.name,
    code: l.product ? productCode(l.product) : l.code || 'MISC',
    sku: l.product?.sku ?? null,
    scan_number: l.product?.scan_number ?? null,
    price: l.price,
    qty: l.qty,
    tax: lineTax(l),
    tax_percent: l.taxPercent,
    total_price: lineNet(l),
    discount_percent: l.discountPercent,
    discount_amount: lineDiscount(l),
  })), [lines])

  // Save (or create) the current cart as an "In Progress" invoice in the DB so a
  // crash leaves it recoverable — faithful to frmExpressInvoicing.CreateInvoice.
  const persistDraft = useCallback(async () => {
    if (committingRef.current || lines.length === 0) return
    let num = invoiceNumberRef.current
    if (invoiceIdRef.current == null && num == null) {
      num = await getNextInvoiceNumber()
      invoiceNumberRef.current = num
      setInvoiceNumber(num)
    }
    const header = {
      legacy_invoice_number: num,
      invoice_date: new Date().toISOString(),
      customer_id: customer?.id ?? null,
      legacy_customer_id: (customer?.legacy_pos_id ?? customer?.legacy_ims_id ?? null) as number | null,
      customer_name: customer ? customerLabel(customer) : null,
      subtotal: totals.subtotal,
      taxable_amount: taxExempt ? 0 : totals.subtotal,
      tax: totals.tax,
      total: totals.grand,
      total_qty: totals.totalQty,
      total_price: totals.subtotal,
      register_id: device?.registerNumber ?? null,
      clerk_id: clerkRef.current?.id || null,
      is_void: false,
      status: 'In Progress',
    }
    const id = await saveDraftInvoice(invoiceIdRef.current, header, buildItemRows())
    invoiceIdRef.current = id
  }, [lines, totals, customer, taxExempt, device, buildItemRows])

  // Void current transaction (legacy cancel): persist the draft, flag it as void,
  // clear the cart, and log the clerk out so the next transaction requires a PIN.
  const voidCurrentTransaction = useCallback(async () => {
    if (lines.length === 0) { toast.info('Nothing to void'); return }
    if (!window.confirm('Void this transaction? This cannot be undone.')) return
    committingRef.current = true
    try {
      await persistDraft()
      const id = invoiceIdRef.current
      if (id) await voidInvoice(id)
      toast.success(`Transaction #${invoiceNumberRef.current ?? ''} voided`)
    } catch (e) {
      console.error('void current transaction failed', e)
      toast.error('Failed to void transaction')
      return
    } finally { committingRef.current = false }
    resetCart()
    setClerkPromptOpen(true)
  }, [lines.length, persistDraft, resetCart, setClerkPromptOpen])

  // Debounced live-save whenever the cart changes (skips empty carts).
  useEffect(() => {
    if (lines.length === 0) return
    const h = setTimeout(() => { persistDraft().catch(e => console.error('draft save', e)) }, 600)
    return () => clearTimeout(h)
  }, [persistDraft, lines.length])

  // Best-effort: flag the live invoice "Interrupted" if the tab closes mid-sale.
  // ("In Progress" is also resumable if this doesn't land before unload.)
  useEffect(() => {
    const handler = () => {
      const id = invoiceIdRef.current
      if (id && !committingRef.current) setInvoiceStatus(id, 'Interrupted').catch(() => {})
    }
    window.addEventListener('pagehide', handler)
    return () => window.removeEventListener('pagehide', handler)
  }, [])

  // Load a held/interrupted/in-progress invoice into the cart (legacy recall).
  const loadDraft = useCallback(async (id: string) => {
    const res = await getInvoiceWithItems(id)
    if (!res) { toast.error('Transaction not found'); return }
    const { invoice, items } = res
    committingRef.current = true
    try {
      invoiceIdRef.current = invoice.id
      invoiceNumberRef.current = invoice.legacy_invoice_number ?? null
      setInvoiceNumber(invoice.legacy_invoice_number ?? null)
      setTaxExempt(false); setSubDiscountPct('')
      setLines(items.map(it => ({
        uid: newUid(),
        product: null,
        productId: (it.product_id as string | null) ?? null,
        code: it.code || it.scan_number || it.sku || it.legacy_product_code?.toString() || (it.product_id ? '#' : 'MISC'),
        name: it.product_name ?? '',
        qty: it.qty ?? 0,
        price: it.price ?? 0,
        discountPercent: it.discount_percent ?? 0,
        discountAmount: it.discount_amount ?? 0,
        taxPercent: it.tax_percent ?? 0,
      })))
      if (invoice.customer_id) { try { setCustomer(await getCustomer(invoice.customer_id)) } catch { /* ignore */ } }
      else setCustomer(null)
      await setInvoiceStatus(invoice.id, 'In Progress')
    } finally {
      committingRef.current = false
    }
    codeRef.current?.focus()
  }, [])

  // Deep-link recall: /sales?resume=<id> (from Transaction Management's "continue"
  // on a Suspended/In Progress/Interrupted row) loads that specific transaction
  // back into the cart. Runs once and wins over the generic crash-recovery below.
  useEffect(() => {
    const rid = searchParams.get('resume')
    if (!rid || resumeReqRef.current === rid) return
    resumeReqRef.current = rid
    recoveredRef.current = true   // suppress generic crash-recovery for this mount
    ;(async () => {
      try { await loadDraft(rid) }
      catch (e) { console.error('resume failed', e) }
      finally {
        const next = new URLSearchParams(searchParams)
        next.delete('resume')
        setSearchParams(next, { replace: true })
      }
    })()
  }, [searchParams, loadDraft, setSearchParams])

  // Crash recovery: on entering the register, auto-load the most recent
  // In Progress / Interrupted transaction for this lane (runs once per mount).
  useEffect(() => {
    if (!device || recoveredRef.current) return
    recoveredRef.current = true
    ;(async () => {
      try {
        const resumable = await listResumableInvoices(device.registerNumber)
        const recover = resumable.find(r => r.status === 'In Progress' || r.status === 'Interrupted')
        if (recover) {
          await loadDraft(recover.id)
          toast.info(`Recovered transaction #${recover.legacy_invoice_number ?? ''}`)
        }
      } catch (e) { console.error('recover failed', e) }
    })()
  }, [device, loadDraft])

  // Suspend / hold the current transaction (legacy btnSuspendTransaction_Click).
  const suspendSale = useCallback(async () => {
    if (lines.length === 0) { toast.info('Nothing to suspend'); return }
    try {
      await persistDraft()
      const id = invoiceIdRef.current
      if (id) await setInvoiceStatus(id, 'Suspended')
      toast.success(`Transaction #${invoiceNumberRef.current ?? ''} suspended`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Suspend failed')
      return
    }
    resetCart()
  }, [lines.length, persistDraft, resetCart])

  // Recall a held transaction; auto-suspend the current cart first so it's kept.
  const onResume = useCallback(async (id: string) => {
    setResumeOpen(false)
    if (lines.length > 0 && invoiceIdRef.current && invoiceIdRef.current !== id) {
      try { await persistDraft(); await setInvoiceStatus(invoiceIdRef.current, 'Suspended') } catch { /* ignore */ }
    }
    await loadDraft(id)
  }, [lines.length, persistDraft, loadDraft])

  // Complete the sale: finalize the in-progress invoice as Completed + payments.
  const completeSale = useCallback(async (payments: Tender[], totalTendered: number, change: number) => {
    committingRef.current = true
    try {
      await finalizeSale({
        invoiceId: invoiceIdRef.current,
        invoiceNumber: invoiceNumberRef.current ?? await getNextInvoiceNumber(),
        clerkId: clerkRef.current?.id ?? null,
        lines, totals, taxRate, taxExempt, refundMode, customer,
        cashierEmail: user?.email ?? undefined, payments, totalTendered, change,
        registerNumber: device?.registerNumber ?? null,
      })
      setTenderOpen(false)
      resetCart()
    } finally {
      committingRef.current = false
    }
  }, [lines, totals, taxRate, taxExempt, refundMode, customer, user, device, resetCart])

  // Register quick-keys (faithful to the legacy POS function-key shortcuts):
  //   F1 Product Search · F2 Tax Exempt · F3 Manual Item · F4 Customer Lookup ·
  //   F8 Suspend/Hold · F12 Tender/Pay · Esc closes the top-most dialog.
  // Action keys are ignored while a dialog/PIN gate is open (Esc handles those).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (searchOpen) { setSearchOpen(false) }
        else if (customerOpen) { setCustomerOpen(false) }
        else if (tenderOpen) { setTenderOpen(false) }
        else if (resumeOpen) { setResumeOpen(false) }
        else if (zoutOpen) { setZoutOpen(false) }
        else if (signInOpen) { setSignInOpen(false) }
        else if (drawerModal) { setDrawerModal(null) }
        return
      }
      const blocked = searchOpen || customerOpen || tenderOpen || resumeOpen || zoutOpen || signInOpen || clerkPromptOpen || drawerModal != null
      switch (e.key) {
        case 'F1': e.preventDefault(); if (!blocked) setSearchOpen(true); break
        case 'F2': e.preventDefault(); if (!blocked) setTaxExempt(v => !v); break
        case 'F3': e.preventDefault(); if (!blocked) addManualLine(); break
        case 'F4': e.preventDefault(); if (!blocked) setCustomerOpen(true); break
        case 'F8': e.preventDefault(); if (!blocked && lines.length > 0) suspendSale(); break
        case 'F12': e.preventDefault(); if (!blocked && lines.length > 0) setTenderOpen(true); break
        default: break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [searchOpen, customerOpen, tenderOpen, resumeOpen, zoutOpen, signInOpen, clerkPromptOpen, drawerModal, lines.length, suspendSale, addManualLine])

  // Prompt for the clerk PIN when entering the register (legacy frmLoginPopUp on
  // StartNewTransaction). Lazy re-prompting on the next item handles later sales.
  useEffect(() => {
    if (device && !clerkRef.current) setClerkPromptOpen(true)
  }, [device])

  // Gate the register until both a device (register) and a clerk are signed in.
  const gated = !device || !clerk

  return (
    <div className="relative flex flex-col h-[calc(100vh-7rem)] gap-3">
      {gated && (
        <div className="absolute inset-0 z-40 bg-white/90 backdrop-blur-sm flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-4">
              <KeyRound size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Register Locked</h3>
            <p className="text-sm text-gray-600 mb-5">
              You must sign into a register and enter a clerk PIN before using the Sales Register.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                className="w-full justify-center"
                onClick={() => setSignInOpen(true)}
                variant={device ? 'secondary' : 'primary'}
              >
                <Monitor size={16} className="mr-2" />
                {device ? `Register ${device.registerNumber} signed in` : 'Sign into Register'}
              </Button>
              <Button
                className="w-full justify-center"
                onClick={() => { pendingCodeRef.current = null; setClerkPromptOpen(true) }}
                variant={clerk ? 'secondary' : 'primary'}
              >
                <KeyRound size={16} className="mr-2" />
                {clerk ? `Clerk: ${clerk.username}` : 'Enter Clerk PIN'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Top bar: status + modes */}
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-xl font-bold text-gray-900 mr-2">Sales Register</h2>
        <button
          onClick={() => { pendingCodeRef.current = null; setClerkPromptOpen(true) }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${clerk ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-amber-500 text-white border-amber-500'}`}
          title="Clerk for this transaction — click to switch / enter PIN"
        >
          <KeyRound size={15} /> {clerk ? `Clerk: ${clerk.username}` : 'Enter Clerk PIN'}
        </button>
        <button
          onClick={() => setSignInOpen(true)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${device ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-amber-500 text-white border-amber-500'}`}
          title="Sign into a register on this device"
        >
          <Monitor size={15} /> {device ? `Register ${device.registerNumber}${device.registerName ? ` · ${device.registerName}` : ''}` : 'Sign into Register'}
        </button>
        {invoiceNumber != null && (
          <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200" title="This transaction is saved and recoverable">
            Txn #{invoiceNumber} · saved
          </span>
        )}
        <div className="flex-1" />
        <button
          onClick={() => navigate('/transactions')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
          title="Go to the transactions list"
        >
          <ClipboardList size={15} /> Transactions
        </button>
        <button
          onClick={() => setResumeOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
          title="Recall a held / interrupted transaction"
        >
          <History size={15} /> Held
        </button>
        <button
          onClick={suspendSale}
          disabled={lines.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          title="Suspend / hold the current transaction (F8)"
        >
          <Pause size={15} /> Suspend
        </button>
        <button
          onClick={() => setDrawerModal(drawer ? 'close' : 'open')}
          disabled={!device}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border disabled:opacity-50 ${drawer ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          title={drawer ? 'Close the cash drawer (blind count)' : 'Open the register with starting cash'}
        >
          <Wallet size={15} /> {drawer ? `Drawer Open · ${currency(drawer.starting_cash)}` : 'Open Drawer'}
        </button>
        <button
          onClick={() => setZoutOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
          title="Z-Out (end of day) — by register or all, date range"
        >
          <FileText size={15} /> Z-Out
        </button>
        <button
          onClick={() => setRefundMode(v => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${refundMode ? 'bg-red-600 text-white border-red-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          <RotateCcw size={15} /> Refund Mode {refundMode ? 'ON' : 'OFF'}
        </button>
        <button
          onClick={() => setTaxExempt(v => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${taxExempt ? 'bg-amber-500 text-white border-amber-500' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          title="F2 — Tax exempt"
        >
          <Ban size={15} /> Tax Exempt {taxExempt ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* Entry bar */}
      <Card className="p-3">
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-4">
            <label className="text-xs font-medium text-gray-500">Product Code / Scan</label>
            <div className="relative">
              <input
                ref={codeRef}
                value={code}
                onChange={e => setCode(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') submitCode() }}
                placeholder="Scan or enter code, Enter to add…"
                className="w-full mt-1 pl-3 pr-9 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button onClick={() => setSearchOpen(true)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-green-700" title="Search products">
                <Search size={18} />
              </button>
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-gray-500">Quantity</label>
            <input value={qty} onChange={e => setQty(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submitCode() }}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-gray-500">Price (override)</label>
            <input value={price} onChange={e => setPrice(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submitCode() }} placeholder="auto"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          <div className="col-span-4 flex gap-2">
            <Button className="flex-1" onClick={submitCode}><Plus size={16} /> Add Item</Button>
            <Button variant="secondary" onClick={addManualLine} title="F3 — manual/misc item">Manual</Button>
          </div>
        </div>
      </Card>

      <div className="flex flex-1 gap-3 min-h-0">
        {/* Items grid */}
        <Card className="flex-1 flex flex-col overflow-hidden">
          <div className="overflow-auto flex-1">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="border-b border-gray-200 text-gray-600">
                  <th className="text-left font-semibold px-3 py-2">Code</th>
                  <th className="text-left font-semibold px-3 py-2">Description</th>
                  <th className="text-right font-semibold px-3 py-2 w-20">Qty</th>
                  <th className="text-right font-semibold px-3 py-2 w-24">Price</th>
                  <th className="text-right font-semibold px-3 py-2 w-20">Disc %</th>
                  <th className="text-right font-semibold px-3 py-2 w-24">Disc $</th>
                  <th className="text-right font-semibold px-3 py-2 w-28">Total</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-gray-400 py-16">Scan or search a product to begin</td></tr>
                ) : lines.map(l => (
                  <tr key={l.uid} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-gray-500">{l.code}</td>
                    <td className="px-3 py-1.5">
                      {l.product ? l.name : (
                        <input value={l.name} onChange={e => updateLine(l.uid, { name: e.target.value })}
                          className="w-full px-2 py-1 rounded border border-gray-200" />
                      )}
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={l.qty} onChange={e => updateLine(l.uid, { qty: parseFloat(e.target.value) || 0 })}
                        className="w-full text-right px-2 py-1 rounded border border-gray-200" />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={l.price} readOnly={!!l.product && !can('perm_change_price_on_plu')}
                        title={!l.product ? 'Enter price for this manual item' : can('perm_change_price_on_plu') ? undefined : 'No permission to change price'}
                        onChange={e => updateLine(l.uid, { price: parseFloat(e.target.value) || 0 })}
                        className={`w-full text-right px-2 py-1 rounded border border-gray-200 ${!l.product || can('perm_change_price_on_plu') ? '' : 'bg-gray-100 text-gray-500'}`} />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={l.discountPercent} readOnly={!can('perm_percent_discount')}
                        title={can('perm_percent_discount') ? undefined : 'No permission to apply a percent discount'}
                        onChange={e => updateLine(l.uid, { discountPercent: parseFloat(e.target.value) || 0 })}
                        className={`w-full text-right px-2 py-1 rounded border border-gray-200 ${can('perm_percent_discount') ? '' : 'bg-gray-100 text-gray-500'}`} />
                    </td>
                    <td className="px-1 py-1.5">
                      <input type="number" value={l.discountAmount} readOnly={!can('perm_dollar_off_discount')}
                        title={can('perm_dollar_off_discount') ? undefined : 'No permission to apply a dollar-off discount'}
                        onChange={e => updateLine(l.uid, { discountAmount: parseFloat(e.target.value) || 0 })}
                        className={`w-full text-right px-2 py-1 rounded border border-gray-200 ${can('perm_dollar_off_discount') ? '' : 'bg-gray-100 text-gray-500'}`} />
                    </td>
                    <td className="px-3 py-1.5 text-right font-medium">{currency(lineNet(l))}</td>
                    <td className="px-1 py-1.5 text-center">
                      <button onClick={() => removeLine(l.uid)} className="text-gray-400 hover:text-red-600"><X size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Totals / actions */}
        <div className="w-80 flex flex-col gap-3">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <input value={subDiscountPct} onChange={e => setSubDiscountPct(e.target.value)} placeholder="Subtotal disc %"
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm" />
              <Button variant="secondary" onClick={applySubDiscount}><Percent size={15} /></Button>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-600"><span>Total Quantity</span><span>{totals.totalQty}</span></div>
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{currency(totals.subtotal)}</span></div>
              <div className="flex justify-between text-gray-600">
                <span>Tax{taxExempt ? ' (exempt)' : ` (${taxRate}%)`}</span><span>{currency(totals.tax)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t border-gray-200">
                <span>Total</span><span>{currency(totals.grand)}</span>
              </div>
            </div>
          </Card>

          <Card className="p-3 space-y-2">
            <Button className="w-full py-3 text-base" disabled={lines.length === 0} onClick={() => setTenderOpen(true)} title="Tender / Pay (F12)">
              <Receipt size={18} /> Tender / Pay
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" disabled={lines.length === 0} onClick={clearSale}><Trash2 size={15} /> Clear</Button>
              <Button variant="secondary" onClick={() => setCustomerOpen(true)}><UserPlus size={15} /> {customer ? customerLabel(customer) : 'Customer'}</Button>
            </div>
            <Button
              className="w-full py-2 text-base bg-red-600 hover:bg-red-700 border-red-600"
              disabled={lines.length === 0}
              onClick={() => guard('perm_void_transaction', 'Void Transaction',
                'You do not have permission to void a transaction. A manager must authorize.', voidCurrentTransaction)}
              title="Void the current transaction and require clerk PIN for the next sale"
            >
              <Ban size={16} /> Void Transaction
            </Button>
            {customer && (
              <div className="flex items-center justify-between text-xs text-gray-600 px-1">
                <span>Store Credit: <span className="font-semibold">{currency(customer.store_credit ?? 0)}</span></span>
                <button onClick={() => setCustomer(null)} className="text-gray-400 hover:text-red-600"><X size={13} /></button>
              </div>
            )}
          </Card>

          <Card className="p-3">
            <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Hot Keys</p>
            <div className="space-y-1 text-xs text-gray-700">
              <div className="flex justify-between"><span className="font-medium">F1</span><span className="text-gray-500">Product Search</span></div>
              <div className="flex justify-between"><span className="font-medium">F2</span><span className="text-gray-500">Tax Exempt</span></div>
              <div className="flex justify-between"><span className="font-medium">F3</span><span className="text-gray-500">Manual Item</span></div>
              <div className="flex justify-between"><span className="font-medium">F4</span><span className="text-gray-500">Customer Lookup</span></div>
              <div className="flex justify-between"><span className="font-medium">F8</span><span className="text-gray-500">Suspend / Hold</span></div>
              <div className="flex justify-between"><span className="font-medium">F12</span><span className="text-gray-500">Tender / Pay</span></div>
              <div className="flex justify-between"><span className="font-medium">Esc</span><span className="text-gray-500">Close dialog</span></div>
            </div>
          </Card>
        </div>
      </div>

      {searchOpen && (
        <ProductSearchModal
          initial={code}
          onClose={() => { setSearchOpen(false); codeRef.current?.focus() }}
          onPick={p => { setSearchOpen(false); addProductLine(p) }}
        />
      )}

      {customerOpen && (
        <CustomerPickerModal
          onClose={() => setCustomerOpen(false)}
          onPick={c => { setCustomer(c); setCustomerOpen(false) }}
        />
      )}

      {tenderOpen && (
        <TenderModal
          grandTotal={totals.grand}
          customer={customer}
          readerOpts={readerOpts}
          onClose={() => setTenderOpen(false)}
          onComplete={completeSale}
        />
      )}

      {signInOpen && (
        <RegisterSignInModal
          current={device}
          onClose={() => setSignInOpen(false)}
          onSignIn={p => { setDeviceRegister(p); setSignInOpen(false) }}
        />
      )}

      {zoutOpen && (
        <ZOutDialog
          defaultRegister={device?.registerNumber ?? 0}
          cashier={user?.email ?? undefined}
          onClose={() => setZoutOpen(false)}
        />
      )}

      {drawerModal === 'open' && device && (
        <OpenDrawerDialog
          registerNumber={device.registerNumber}
          clerk={user?.email ?? undefined}
          onClose={() => setDrawerModal(null)}
          onOpened={s => { setDrawer(s); setDrawerModal(null) }}
        />
      )}

      {drawerModal === 'close' && drawer && (
        <CloseDrawerDialog
          session={drawer}
          clerk={user?.email ?? undefined}
          onClose={() => setDrawerModal(null)}
          onClosed={() => { setDrawer(null); setDrawerModal(null) }}
        />
      )}

      {resumeOpen && (
        <ResumeDialog
          registerNumber={device?.registerNumber ?? 0}
          currentId={invoiceIdRef.current}
          onClose={() => setResumeOpen(false)}
          onResume={onResume}
        />
      )}

      {clerkPromptOpen && (
        <ClerkPinModal
          onClose={() => setClerkPromptOpen(false)}
          onVerified={onClerkVerified}
        />
      )}

      {override && (
        <ManagerOverride
          perm={override.perm}
          title={`Manager Authorization — ${override.title}`}
          message={override.message}
          onApproved={() => { const run = override.run; setOverride(null); run() }}
          onClose={() => setOverride(null)}
        />
      )}
    </div>
  )
}

// ---- Finalize -----------------------------------------------------------------

interface Tender {
  method: string
  amount: number
  // Optional tender metadata (faithful to tblPaid extras).
  checkNumber?: string       // Check / gift-cert number -> payments.check_number
  cardType?: string          // Card brand or remaining gift-cert balance -> payments.card_type
  giftCertId?: string        // For decrementing the certificate balance on completion
  reason?: string            // Donation reason -> payments.ihs_reason
}

async function finalizeSale(args: {
  invoiceId: string | null
  invoiceNumber: number
  clerkId?: string | null
  lines: SaleLine[]
  totals: { totalQty: number; subtotal: number; tax: number; grand: number }
  taxRate: number
  taxExempt: boolean
  refundMode: boolean
  customer: Customer | null
  cashierEmail?: string
  payments: Tender[]
  totalTendered: number
  change: number
  registerNumber?: number | null
}) {
  const { invoiceId, invoiceNumber, clerkId, lines, totals, taxExempt, customer, payments, totalTendered, change, cashierEmail, registerNumber } = args
  try {
    const now = new Date().toISOString()
    const hasCash = payments.some(p => p.method === 'Cash')

    // Finalize the in-progress invoice (created live as the cart was built) as
    // Completed, rather than inserting a brand-new invoice.
    await finalizeDraftInvoice(
      invoiceId,
      {
        legacy_invoice_number: invoiceNumber,
        invoice_date: now,
        customer_id: customer?.id ?? null,
        legacy_customer_id: (customer?.legacy_pos_id ?? customer?.legacy_ims_id ?? null) as number | null,
        customer_name: customer ? customerLabel(customer) : null,
        subtotal: totals.subtotal,
        taxable_amount: taxExempt ? 0 : totals.subtotal,
        tax: totals.tax,
        total: totals.grand,
        total_qty: totals.totalQty,
        total_price: totals.subtotal,
        payment_method: payments.map(p => p.method).join(', '),
        register_id: registerNumber ?? null,
        clerk_id: clerkId || null,
        is_void: false,
        status: 'Completed',
      },
      lines.map(l => ({
        product_id: l.product?.id ?? l.productId ?? null,
        product_name: l.name,
        code: l.product ? productCode(l.product) : l.code || 'MISC',
        sku: l.product?.sku ?? null,
        scan_number: l.product?.scan_number ?? null,
        price: l.price,
        qty: l.qty,
        tax: lineTax(l),
        tax_percent: l.taxPercent,
        total_price: lineNet(l),
        discount_percent: l.discountPercent,
        discount_amount: lineDiscount(l),
      })),
      payments.map((p, i) => ({
        payment_method: p.method,
        amount: p.amount,
        amount_tendered: p.method === 'Cash' && i === payments.length - 1 ? p.amount + change : p.amount,
        change_due: p.method === 'Cash' ? change : 0,
        check_number: p.checkNumber ?? null,
        card_type: p.cardType ?? null,
        ihs_reason: p.reason ?? null,
        customer_name: customer ? customerLabel(customer) : null,
        customer_code: customer?.customer_code ?? null,
        register_number: registerNumber != null ? String(registerNumber) : null,
        transaction_date: now,
        status: 'Approved',
      })),
    )

    // Faithful side-effects: deduct store credit, decrement gift certs, charge AR.
    for (const p of payments) {
      if (p.method === 'Store Credit' && customer) {
        try { await adjustCustomerStoreCredit(customer.id, -p.amount) } catch (e) { console.error('store credit adjust failed', e) }
      }
      if (p.method === 'Gift Card' && p.giftCertId) {
        try { await redeemGiftCertificate(p.giftCertId, p.amount) } catch (e) { console.error('gift cert redeem failed', e) }
      }
      if (p.method === 'In-House Charge' && customer) {
        try { await adjustCustomerArBalance(customer.id, p.amount) } catch (e) { console.error('AR charge failed', e) }
      }
    }

    if (hasCash) await openCashDrawer()

    await printReceipt({
      invoiceNumber,
      date: new Date().toLocaleString(),
      cashier: cashierEmail,
      lines: lines.map(l => ({ name: l.name, qty: l.qty, price: l.price, total: lineNet(l) })),
      subtotal: totals.subtotal,
      tax: totals.tax,
      total: totals.grand,
      tendered: totalTendered,
      change,
      paymentMethod: payments.map(p => p.method).join(', '),
    })

    toast.success(`Sale completed — Invoice #${invoiceNumber}`)
  } catch (e) {
    console.error(e)
    toast.error('Failed to complete sale')
    throw e
  }
}

// ---- Product search modal (frmProductDashboard lookup) ------------------------

function ProductSearchModal({ initial, onClose, onPick }: { initial: string; onClose: () => void; onPick: (p: Product) => void }) {
  const [term, setTerm] = useState(initial)
  const [results, setResults] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true)
      try { setResults(await searchProducts(term, 40)) } finally { setLoading(false) }
    }, 200)
    return () => clearTimeout(t)
  }, [term])

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-semibold">Product Search</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-4">
          <input autoFocus value={term} onChange={e => setTerm(e.target.value)} placeholder="Search by name, SKU, or scan…"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
        <div className="overflow-y-auto flex-1 px-4 pb-4">
          {loading ? <p className="text-center text-gray-400 py-8">Searching…</p> : (
            <table className="w-full text-sm">
              <tbody>
                {results.map(p => (
                  <tr key={p.id} onClick={() => onPick(p)} className="cursor-pointer hover:bg-green-50 border-b border-gray-100">
                    <td className="px-2 py-2 text-gray-500 w-28">{productCode(p)}</td>
                    <td className="px-2 py-2">{p.name}</td>
                    <td className="px-2 py-2 text-right font-medium w-24">{currency(p.sale_price ?? p.retail_price ?? 0)}</td>
                  </tr>
                ))}
                {!results.length && <tr><td className="text-center text-gray-400 py-8">No matches</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  )
}

// ---- Customer picker modal (frmCustomerDashboard lookup) ----------------------

function CustomerPickerModal({ onClose, onPick }: { onClose: () => void; onPick: (c: Customer) => void }) {
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<Customer[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!term.trim()) { setResults([]); return }
    const t = setTimeout(async () => {
      setLoading(true)
      try { const { rows } = await listCustomers({ search: term, page: 0, pageSize: 30 }); setResults(rows) }
      finally { setLoading(false) }
    }, 200)
    return () => clearTimeout(t)
  }, [term])

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-semibold">Customer Lookup</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-4">
          <input autoFocus value={term} onChange={e => setTerm(e.target.value)} placeholder="Search by name, company, phone, email…"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
        <div className="overflow-y-auto flex-1 px-4 pb-4">
          {loading ? <p className="text-center text-gray-400 py-8">Searching…</p> : (
            <table className="w-full text-sm">
              <tbody>
                {results.map(c => (
                  <tr key={c.id} onClick={() => onPick(c)} className="cursor-pointer hover:bg-green-50 border-b border-gray-100">
                    <td className="px-2 py-2">{customerLabel(c)}</td>
                    <td className="px-2 py-2 text-gray-500">{[c.city, c.state].filter(Boolean).join(', ')}</td>
                    <td className="px-2 py-2 text-gray-500 w-32">{c.phone_home ?? c.mobile ?? c.phone_work ?? ''}</td>
                    <td className="px-2 py-2 text-right w-24 font-medium">{currency(c.store_credit ?? 0)}</td>
                  </tr>
                ))}
                {!results.length && term.trim() && <tr><td className="text-center text-gray-400 py-8">No matches</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  )
}

// ---- Tender modal (frmPaymentMethod) — split tender ---------------------------

const PAYMENT_METHODS = ['Cash', 'Credit Card', 'Debit Card', 'Check', 'Gift Card', 'Store Credit', 'Guarantee', 'In-House Charge', 'Donation']

function TenderModal({ grandTotal, customer, readerOpts, onClose, onComplete }: {
  grandTotal: number
  customer: Customer | null
  readerOpts?: ChargeOptions
  onClose: () => void
  onComplete: (payments: Tender[], totalTendered: number, change: number) => Promise<void>
}) {
  const [payments, setPayments] = useState<Tender[]>([])
  const [method, setMethod] = useState('Cash')
  const [amount, setAmount] = useState('')
  const [processing, setProcessing] = useState(false)
  const [charging, setCharging] = useState(false)
  const [motoCharging, setMotoCharging] = useState(false)
  const cardTerminalReady = isStripeTerminalConfigured()
  const motoReady = isStripeMotoConfigured()
  // Append the legacy "Manual Stripe" (phone/mail order) method only when MOTO is configured.
  const methods = motoReady ? [...PAYMENT_METHODS, 'Manual Stripe'] : PAYMENT_METHODS

  const paid = payments.reduce((s, p) => s + p.amount, 0)
  const balance = Math.round((grandTotal - paid) * 100) / 100
  const amountNum = parseFloat(amount) || 0
  // Cash overpayment on the final balance produces change due.
  const change = method === 'Cash' && amountNum > balance ? Math.round((amountNum - balance) * 100) / 100 : 0

  // Store credit already staged on this sale (so split tenders don't overdraw).
  const storeCreditUsed = payments.filter(p => p.method === 'Store Credit').reduce((s, p) => s + p.amount, 0)

  const addPayment = async () => {
    const amt = amountNum || balance
    if (amt <= 0) return

    // Store Credit (ProcessStoreCredit): requires a customer and enough balance.
    if (method === 'Store Credit') {
      if (!customer) { toast.error('Select a customer first (Customer button).'); return }
      const available = Math.round(((customer.store_credit ?? 0) - storeCreditUsed) * 100) / 100
      const applied = Math.min(amt, balance)
      if (applied > available) {
        toast.error('The customer does not have enough store credit for this transaction')
        return
      }
      setPayments(prev => [...prev, { method, amount: applied }])
      setAmount('')
      return
    }

    // Gift Card (GiftCertificate_Click + ProcessGiftCard): look up the cert,
    // verify a positive balance, and tender min(cert balance, remaining).
    if (method === 'Gift Card') {
      const entered = window.prompt('Please enter # or swipe gift certificate now')
      if (entered == null || entered.trim() === '') return
      try {
        const gc = await lookupGiftCertificate(entered)
        if (!gc) { toast.error('This Gift Certificate is not in the system.'); return }
        const certBalance = Number(gc.current_balance ?? 0)
        if (certBalance <= 0) { toast.error('This Gift Certificate has a 0 (ZERO) balance.'); return }
        const applied = Math.min(certBalance, balance)
        const remaining = Math.round((certBalance - applied) * 100) / 100
        setPayments(prev => [...prev, {
          method, amount: applied, giftCertId: gc.id,
          checkNumber: gc.certificate_number ?? entered.trim(),
          cardType: remaining.toString(),
        }])
        if (remaining <= 0) toast.info('This gift card will have a $0 balance after this sale.')
        setAmount('')
      } catch {
        toast.error('Gift certificate lookup failed')
      }
      return
    }

    // Check (ProcessCheck -> frmCheck): capture the check number.
    if (method === 'Check') {
      const checkNumber = window.prompt('Enter check number') ?? ''
      setPayments(prev => [...prev, { method, amount: Math.min(amt, balance), checkNumber: checkNumber.trim() || undefined }])
      setAmount('')
      return
    }

    // In-House Charge: charge the customer's house account (AR balance).
    if (method === 'In-House Charge') {
      if (!customer) { toast.error('Select a customer first (Customer button).'); return }
      setPayments(prev => [...prev, { method, amount: Math.min(amt, balance) }])
      setAmount('')
      return
    }

    // Donation (ProcessDonation): confirm, capture a reason, tender the balance.
    if (method === 'Donation') {
      if (!window.confirm('Are you sure you want to donate the items on this invoice?')) return
      const reason = window.prompt('Donation reason (optional)') ?? ''
      setPayments(prev => [...prev, { method, amount: balance, reason: reason.trim() || undefined }])
      setAmount('')
      return
    }

    // Cash/Guarantee and other methods cap at the remaining balance.
    setPayments(prev => [...prev, { method, amount: Math.min(amt, balance) }])
    setAmount('')
  }

  // Card-present charge on the Stripe Terminal reader for the remaining balance.
  const chargeCardTerminal = async () => {
    const amt = amountNum > 0 ? amountNum : balance
    if (amt <= 0) return
    setCharging(true)
    try {
      const res = await chargeOnTerminal(Math.round(amt * 100), "Thomson's POS sale", readerOpts)
      if (res.status === 'succeeded' || res.status === 'requires_capture') {
        setPayments(prev => [...prev, { method: method === 'Debit Card' ? 'Debit Card' : 'Credit Card', amount: Math.min(amt, balance) }])
        setAmount('')
        toast.success('Card approved')
      } else {
        toast.error(`Card not completed (${res.status})`)
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Card terminal error')
    } finally {
      setCharging(false)
    }
  }

  // Server-driven reader charge (legacy frmPaymentMethod flow): create intent ->
  // reader.process_payment_intent with process_config[moto]=true -> poll -> capture.
  // The legacy POS used this SAME path for in-person Credit/Debit swipes AND for
  // phone/mail ("Manual Stripe") orders, keyed only by the register's reader id.
  const chargeViaReader = async (recordMethod: string) => {
    const amt = amountNum > 0 ? amountNum : balance
    if (amt <= 0) return
    const isPhone = recordMethod === 'Manual Stripe'
    setMotoCharging(true)
    try {
      const res = await chargeMotoOnReader(Math.round(amt * 100), isPhone ? "Thomson's POS phone/mail order" : "Thomson's POS sale", readerOpts)
      if (res.status === 'succeeded') {
        setPayments(prev => [...prev, { method: recordMethod, amount: Math.min(amt, balance) }])
        setAmount('')
        toast.success(isPhone ? `Phone order approved (${res.cardType})` : 'Card approved')
      } else if (res.status === 'timedout') {
        toast.error('Reader timed out — have the customer re-tap/insert the card and try again')
      } else {
        toast.error(`Card not completed (${res.status})`)
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Card reader error — verify the reader is on and online')
    } finally {
      setMotoCharging(false)
    }
  }

  // Methods that need explicit staging (prompts / balance checks / reader) and
  // must NOT be auto-tendered by the one-click Complete fallback. When ANY card
  // reader path is configured, Credit/Debit are forced through the charge button
  // so a card is never marked paid without actually being charged.
  const cardReaderReady = motoReady || cardTerminalReady
  const explicitMethods = useMemo(() => {
    const s = new Set(['Store Credit', 'Gift Card', 'Check', 'Manual Stripe', 'In-House Charge', 'Donation'])
    if (cardReaderReady) { s.add('Credit Card'); s.add('Debit Card') }
    return s
  }, [cardReaderReady])

  const complete = async () => {
    let pmts = payments
    // Allow one-click complete: if nothing staged, tender the full balance with selected method.
    if (pmts.length === 0 || balance > 0.001) {
      if (explicitMethods.has(method)) {
        toast.error(cardReaderReady && (method === 'Credit Card' || method === 'Debit Card')
          ? 'Use the "Charge on Card Reader" button to run this card.'
          : `Use "Add Split" / the charge button to apply ${method}.`)
        return
      }
      const amt = amountNum || balance
      if (method === 'Cash' || amt >= balance) {
        pmts = [...payments, { method, amount: Math.min(amt, balance) }]
      } else {
        toast.error('Remaining balance must be fully tendered')
        return
      }
    }
    setProcessing(true)
    try {
      const totalTendered = pmts.reduce((s, p) => s + p.amount, 0) + change
      await onComplete(pmts, totalTendered, change)
    } finally {
      setProcessing(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">Tender</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex justify-between text-sm text-gray-600"><span>Total Due</span><span className="font-semibold">{currency(grandTotal)}</span></div>
          <div className="flex justify-between text-lg font-bold"><span>Balance</span><span className={balance <= 0 ? 'text-green-600' : 'text-gray-900'}>{currency(Math.max(0, balance))}</span></div>

          {payments.length > 0 && (
            <div className="space-y-1 border-y border-gray-100 py-2">
              {payments.map((p, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-gray-600">{p.method}</span>
                  <span className="flex items-center gap-2">{currency(p.amount)}
                    <button onClick={() => setPayments(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600"><X size={13} /></button>
                  </span>
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700">Payment Method</label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {methods.map(m => (
                <button key={m} onClick={() => setMethod(m)}
                  className={`px-2 py-2 rounded-lg text-xs font-medium border ${method === m ? 'bg-green-700 text-white border-green-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700">Amount Tendered</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder={currency(Math.max(0, balance))} autoFocus
                className="w-full mt-1 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <Button variant="secondary" onClick={addPayment}>Add Split</Button>
          </div>

          {/* Preferred path (faithful to legacy): server-driven reader charge by reader id. */}
          {motoReady && (method === 'Credit Card' || method === 'Debit Card') && (
            <Button variant="secondary" className="w-full" disabled={motoCharging || balance <= 0} onClick={() => chargeViaReader(method)}>
              {motoCharging ? 'Charging on reader…' : `Charge ${currency(amountNum > 0 ? amountNum : Math.max(0, balance))} on Card Reader`}
            </Button>
          )}

          {/* Fallback: browser Terminal SDK (only when reader-process isn't configured). */}
          {!motoReady && cardTerminalReady && (method === 'Credit Card' || method === 'Debit Card') && (
            <Button variant="secondary" className="w-full" disabled={charging || balance <= 0} onClick={chargeCardTerminal}>
              {charging ? 'Waiting for card…' : `Charge ${currency(amountNum > 0 ? amountNum : Math.max(0, balance))} on Card Terminal`}
            </Button>
          )}

          {!cardReaderReady && (method === 'Credit Card' || method === 'Debit Card') && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              No card reader is configured for this register, so the app cannot charge the card here. Completing will <b>record</b> this as paid only — use this just when you ran the card on a separate terminal. To charge the Wise/BBPOS reader from here, set the reader id in Settings → Registers (and the Stripe env URLs).
            </p>
          )}

          {motoReady && method === 'Manual Stripe' && (
            <Button variant="secondary" className="w-full" disabled={motoCharging || balance <= 0} onClick={() => chargeViaReader('Manual Stripe')}>
              {motoCharging ? 'Key card on terminal…' : `Charge ${currency(amountNum > 0 ? amountNum : Math.max(0, balance))} — Phone/Mail Order`}
            </Button>
          )}

          {change > 0 && (
            <div className="flex justify-between text-sm"><span className="text-gray-600">Change Due</span><span className="font-semibold text-green-600">{currency(change)}</span></div>
          )}

          <Button className="w-full py-3" disabled={processing} onClick={complete}>
            {processing ? 'Processing…' : 'Complete Sale & Print Receipt'}
          </Button>
        </div>
      </Card>
    </div>
  )
}

// ---- Register sign-in (per-device) --------------------------------------------
// Picks which register lane THIS machine is, plus its local receipt printer /
// cash-drawer transport. Saved to localStorage (faithful to the legacy POS
// per-machine My.Settings). The lane's Stripe reader is resolved from the DB.

function RegisterSignInModal({ current, onClose, onSignIn }: {
  current: DeviceRegisterProfile | null
  onClose: () => void
  onSignIn: (p: DeviceRegisterProfile) => void
}) {
  const [registers, setRegisters] = useState<Register[]>([])
  const [loading, setLoading] = useState(true)
  const [registerNumber, setRegisterNumber] = useState<number | ''>(current?.registerNumber ?? '')
  const [printerType, setPrinterType] = useState<PrinterType>(current?.printerType ?? 'serial')
  const [drawerType, setDrawerType] = useState<DrawerType>(current?.drawerType ?? 'serial')

  useEffect(() => {
    listRegisters(true)
      .then(rs => {
        setRegisters(rs)
        if (registerNumber === '' && rs.length) setRegisterNumber(rs[0].register_number)
      })
      .catch(() => setRegisters([]))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selected = registers.find(r => r.register_number === registerNumber)

  const save = () => {
    if (registerNumber === '') { toast.error('Select a register'); return }
    onSignIn({
      registerNumber: Number(registerNumber),
      registerName: selected?.name ?? null,
      printerType,
      drawerType,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">Sign into Register</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          {loading ? (
            <p className="text-center text-gray-400 py-6">Loading registers…</p>
          ) : registers.length === 0 ? (
            <p className="text-sm text-amber-600">No registers are configured yet. Add one in Settings → Registers (admin).</p>
          ) : (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700">Register (this device)</label>
                <select value={registerNumber} onChange={e => setRegisterNumber(Number(e.target.value))}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  {registers.map(r => (
                    <option key={r.id} value={r.register_number}>
                      Register {r.register_number}{r.name ? ` · ${r.name}` : ''}{r.stripe_reader_id ? ' (reader linked)' : ''}
                    </option>
                  ))}
                </select>
                {selected && (
                  <p className="text-xs text-gray-400 mt-1">
                    Card reader: {selected.stripe_reader_id ? selected.stripe_reader_id : 'none assigned (uses default)'}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Receipt Printer</label>
                  <select value={printerType} onChange={e => setPrinterType(e.target.value as PrinterType)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm">
                    <option value="serial">Serial (ESC/POS)</option>
                    <option value="browser">Browser print</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Cash Drawer</label>
                  <select value={drawerType} onChange={e => setDrawerType(e.target.value as DrawerType)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm">
                    <option value="serial">Serial kick</option>
                    <option value="none">None</option>
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-400">Saved to this device only. Sales rung here are stamped with this register number, and card charges use the register's linked Stripe reader.</p>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={loading || registers.length === 0}>Sign in</Button>
        </div>
      </Card>
    </div>
  )
}

// ---- Resume / recall held transactions (legacy Suspend recall) ----------------
// Lists Suspended / Interrupted / In Progress invoices for the lane so the clerk
// can recall one back into the cart. Excludes the currently-loaded transaction.

function ResumeDialog({ registerNumber, currentId, onClose, onResume }: {
  registerNumber: number
  currentId: string | null
  onClose: () => void
  onResume: (id: string) => void
}) {
  const [rows, setRows] = useState<ResumableInvoice[] | null>(null)

  useEffect(() => {
    listResumableInvoices(registerNumber)
      .then(r => setRows(r.filter(x => x.id !== currentId)))
      .catch(() => setRows([]))
  }, [registerNumber, currentId])

  const badge = (status: string | null) => {
    const map: Record<string, string> = {
      Suspended: 'bg-blue-50 text-blue-700 border-blue-200',
      Interrupted: 'bg-amber-50 text-amber-700 border-amber-200',
      'In Progress': 'bg-gray-100 text-gray-600 border-gray-200',
    }
    return map[status ?? ''] ?? 'bg-gray-100 text-gray-600 border-gray-200'
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">Held Transactions</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto">
          {rows === null ? (
            <p className="text-sm text-gray-400 text-center py-12">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12">No held or interrupted transactions{registerNumber ? ' for this register' : ''}.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {rows.map(r => (
                <li key={r.id}>
                  <button
                    onClick={() => onResume(r.id)}
                    className="w-full text-left px-5 py-3 hover:bg-green-50 flex items-center justify-between gap-3"
                  >
                    <div>
                      <div className="font-medium text-gray-800">
                        #{r.legacy_invoice_number ?? '—'}
                        {r.customer_name ? <span className="text-gray-500 font-normal"> · {r.customer_name}</span> : null}
                      </div>
                      <div className="text-xs text-gray-500">
                        {r.invoice_date ? new Date(r.invoice_date).toLocaleString() : ''} · {r.total_qty ?? 0} items
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{currency(r.total ?? 0)}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${badge(r.status)}`}>{r.status}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </Card>
    </div>
  )
}

// ---- Z-Out dialog (port of ZOutDiag) ------------------------------------------
// Pick a date range and the register (ALL or a specific lane), generate the
// reconciliation, preview it on screen, then print the faithful receipt.

function ZOutDialog({ defaultRegister, cashier, onClose }: {
  defaultRegister: number
  cashier?: string
  onClose: () => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [registers, setRegisters] = useState<Register[]>([])
  const [startDay, setStartDay] = useState(today)
  const [endDay, setEndDay] = useState(today)
  const [register, setRegister] = useState<number>(defaultRegister)
  const [loading, setLoading] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const [data, setData] = useState<ZoutData | null>(null)

  useEffect(() => {
    listRegisters(true).then(setRegisters).catch(() => setRegisters([]))
  }, [])

  // Re-generating is required whenever the inputs change.
  const generate = async () => {
    if (startDay > endDay) { toast.error('Start date must be on or before end date'); return }
    setLoading(true)
    try {
      const z = await zoutReport(startDay, endDay, register)
      const withClerk = { ...z, cashier }
      setData(withClerk)
      setPreview(formatZReportText(withClerk))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Z-Out failed')
    } finally {
      setLoading(false)
    }
  }

  const print = async () => {
    if (!data) return
    setPrinting(true)
    try {
      await printZReport(data)
      toast.success(`Z-Out printed (${data.register === 'ALL' ? 'All Registers' : 'Register ' + data.register})`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Print failed')
    } finally {
      setPrinting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">Z-Out Report</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Start date</label>
              <input type="date" value={startDay} max={endDay} onChange={e => { setStartDay(e.target.value); setPreview(null) }}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">End date</label>
              <input type="date" value={endDay} min={startDay} onChange={e => { setEndDay(e.target.value); setPreview(null) }}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Register</label>
              <select value={register} onChange={e => { setRegister(Number(e.target.value)); setPreview(null) }}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value={0}>ALL</option>
                {registers.map(r => (
                  <option key={r.id} value={r.register_number}>
                    {r.register_number}{r.name ? ` · ${r.name}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {preview ? (
            <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs leading-snug font-mono whitespace-pre overflow-x-auto">{preview}</pre>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">Choose a date range and register, then <b>Generate</b> to preview the reconciliation.</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button variant="secondary" onClick={generate} disabled={loading}>{loading ? 'Generating…' : 'Generate'}</Button>
          <Button onClick={print} disabled={!preview || printing}>{printing ? 'Printing…' : 'Print Z-Out'}</Button>
        </div>
      </Card>
    </div>
  )
}

// ---- Open Drawer dialog (register session start) ----------------------------
function OpenDrawerDialog({ registerNumber, clerk, onClose, onOpened }: {
  registerNumber: number; clerk?: string; onClose: () => void; onOpened: (s: RegisterSession) => void
}) {
  const [cash, setCash] = useState('')
  const [busy, setBusy] = useState(false)
  const open = async () => {
    const c = parseFloat(cash)
    if (isNaN(c) || c < 0) { toast.error('Enter a starting cash amount'); return }
    setBusy(true)
    try {
      const s = await openRegisterSession({ register_number: registerNumber, opened_by: clerk ?? null, starting_cash: c })
      await openCashDrawer()
      toast.success(`Register ${registerNumber} opened with ${currency(c)}`)
      onOpened(s)
    } catch (e) { console.error(e); toast.error('Could not open drawer — one may already be open for this register.') }
    finally { setBusy(false) }
  }
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">Open Register {registerNumber}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="text-sm block">
            <span className="block text-gray-600 mb-1">Starting cash (drawer float)</span>
            <input type="number" step="0.01" autoFocus value={cash} onChange={e => setCash(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') open() }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="0.00" />
          </label>
          <p className="text-xs text-gray-400">Opening kicks the cash drawer so you can verify the float.</p>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={open} disabled={busy}><Wallet size={15} /> Open Drawer</Button>
        </div>
      </Card>
    </div>
  )
}

// ---- Close Drawer dialog (blind count + over/short) -------------------------
function CloseDrawerDialog({ session, clerk, onClose, onClosed }: {
  session: RegisterSession; clerk?: string; onClose: () => void; onClosed: () => void
}) {
  const [counted, setCounted] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<RegisterSession | null>(null)

  const close = async () => {
    const c = parseFloat(counted)
    if (isNaN(c) || c < 0) { toast.error('Enter the counted cash'); return }
    setBusy(true)
    try {
      const closed = await closeRegisterSession(session, { counted_cash: c, closed_by: clerk ?? null, notes: notes || null })
      setResult(closed)
      await openCashDrawer()
    } catch (e) { console.error(e); toast.error('Failed to close drawer') }
    finally { setBusy(false) }
  }

  const printDrawer = () => {
    if (!result) return
    const os = result.over_short ?? 0
    printHtml(`Drawer Close — Register ${result.register_number}`, `
      <div style="text-align:center"><h2>Drawer Close Report</h2>
        <p>Register ${result.register_number}<br/>${new Date(result.closed_at ?? Date.now()).toLocaleString()}</p></div>
      <table class="totals"><tbody>
        <tr><td class="label">Opened</td><td class="right">${new Date(result.opened_at).toLocaleString()}</td></tr>
        <tr><td class="label">Opened by</td><td class="right">${result.opened_by ?? ''}</td></tr>
        <tr><td class="label">Closed by</td><td class="right">${result.closed_by ?? ''}</td></tr>
        <tr><td class="label">Starting Cash</td><td class="right">${currency(result.starting_cash)}</td></tr>
        <tr><td class="label">Cash Sales</td><td class="right">${currency(result.cash_sales)}</td></tr>
        <tr><td class="label">Cash Payouts</td><td class="right">-${currency(result.payouts_total)}</td></tr>
        <tr><td class="label" style="font-weight:bold">Expected in Drawer</td><td class="right" style="font-weight:bold">${currency(result.expected_cash)}</td></tr>
        <tr><td class="label">Counted</td><td class="right">${currency(result.counted_cash)}</td></tr>
        <tr><td class="label" style="font-weight:bold">${os < 0 ? 'SHORT' : os > 0 ? 'OVER' : 'BALANCED'}</td><td class="right" style="font-weight:bold">${currency(os)}</td></tr>
      </tbody></table>
      ${result.notes ? `<p><b>Notes:</b> ${result.notes}</p>` : ''}`)
  }

  const os = result?.over_short ?? 0
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">Close Register {session.register_number}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {result ? (
          <div className="p-5 space-y-2 text-sm">
            <div className="flex justify-between text-gray-600"><span>Starting Cash</span><span>{currency(result.starting_cash)}</span></div>
            <div className="flex justify-between text-gray-600"><span>Cash Sales</span><span>{currency(result.cash_sales)}</span></div>
            <div className="flex justify-between text-gray-600"><span>Cash Payouts</span><span>-{currency(result.payouts_total)}</span></div>
            <div className="flex justify-between font-semibold text-gray-800 pt-2 border-t border-gray-200"><span>Expected</span><span>{currency(result.expected_cash)}</span></div>
            <div className="flex justify-between text-gray-600"><span>Counted</span><span>{currency(result.counted_cash)}</span></div>
            <div className={`flex justify-between text-lg font-bold pt-2 border-t border-gray-200 ${os < 0 ? 'text-red-600' : os > 0 ? 'text-amber-600' : 'text-green-700'}`}>
              <span>{os < 0 ? 'Short' : os > 0 ? 'Over' : 'Balanced'}</span><span>{currency(os)}</span>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <p className="text-xs text-gray-500">Blind count — enter the total cash counted in the drawer. The expected amount and over/short are revealed after you submit.</p>
            <label className="text-sm block"><span className="block text-gray-600 mb-1">Counted cash</span>
              <input type="number" step="0.01" autoFocus value={counted} onChange={e => setCounted(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="0.00" /></label>
            <label className="text-sm block"><span className="block text-gray-600 mb-1">Notes (optional)</span>
              <input value={notes} onChange={e => setNotes(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></label>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          {result ? (
            <>
              <Button variant="secondary" onClick={printDrawer}><FileText size={15} /> Print</Button>
              <Button onClick={onClosed}>Done</Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button onClick={close} disabled={busy}><Wallet size={15} /> Count &amp; Close</Button>
            </>
          )}
        </div>
      </Card>
    </div>
  )
}
